import deepmerge from 'deepmerge';

var minBreakpoint = function minBreakpoint(breakpoint) {
  return breakpoint !== 0 ? breakpoint : null;
};
var minWidth = function minWidth(value) {
  return "@media (min-width: " + value + "px)";
};
var DEFAULT_BREAKPOINTS = {
  xs: 0,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200
};

var is = function is(n) {
  return n !== undefined && n !== null;
};
var num = function num(n) {
  return typeof n === 'number' && !Number.isNaN(n);
};
var string = function string(n) {
  return typeof n === 'string' && n !== '';
};
var obj = function obj(n) {
  return typeof n === 'object' && n !== null;
};
var func = function func(n) {
  return typeof n === 'function';
};
var negative = function negative(n) {
  return n < 0;
};
var get = function get(obj, path) {
  return String(path).split('.').reduce(function (a, b) {
    return a && is(a[b]) ? a[b] : undefined;
  }, obj);
};
function merge(acc, item) {
  if (!item) {
    return acc;
  }

  return deepmerge(acc, item, {
    clone: false // No need to clone deep, it's way faster.

  });
}

function callOrReturn(fn, arg) {
  if (!func(fn)) return fn;
  var next = fn(arg);
  return callOrReturn(next, arg);
}

function getThemeValue(theme, path, initial) {
  if (!theme) return undefined;
  return callOrReturn(get(initial || theme, path), {
    theme: theme
  });
}

function getValue(value, variants, theme) {
  if (is(variants)) {
    var valueFromVariants = getThemeValue(theme, value, variants);

    if (is(valueFromVariants)) {
      return valueFromVariants;
    }
  }

  return value;
}

function styleFromValue(cssProperties, value, theme, themeKey, transform) {
  var variants = getThemeValue(theme, themeKey);
  var computedValue = getValue(value, variants, theme);

  if (string(computedValue) || num(computedValue)) {
    var _style = {};

    for (var i = 0; i < cssProperties.length; i++) {
      _style[cssProperties[i]] = transform ? transform(computedValue, {
        rawValue: value,
        variants: variants
      }) : computedValue;
    }

    return _style;
  }

  return null;
}

function getThemeFromCssArg(propsOrTheme) {
  if (!propsOrTheme) {
    return null;
  } // Styled Components


  if (propsOrTheme.theme) {
    return propsOrTheme.theme;
  } // Emotion


  return propsOrTheme;
}

function getBreakpoints(theme) {
  var themeBreakpoints = getThemeValue(theme, 'breakpoints');

  if (is(themeBreakpoints)) {
    return themeBreakpoints;
  }

  return DEFAULT_BREAKPOINTS;
}

function createStyleGenerator(getStyle, props, generators) {
  var getStyles = function getStyles(attrs) {
    return function (propsOrTheme) {
      var theme = getThemeFromCssArg(propsOrTheme);
      return getStyle(attrs, theme);
    };
  };

  var getStylesFromProps = function getStylesFromProps(props) {
    var theme = props.theme || null;
    return getStyle(props, theme);
  };

  getStyles.meta = {
    props: props,
    getStyle: getStyle,
    generators: generators
  };
  getStyles.props = getStylesFromProps;
  return getStyles;
}

function styleFromBreakPoint(cssProperties, value, theme, themeKey, transform) {
  var breakpoints = getBreakpoints(theme);
  var keys = Object.keys(value);
  var allStyle = {};

  for (var i = 0; i < keys.length; i++) {
    var breakpoint = keys[i];

    var _style2 = styleFromValue(cssProperties, value[breakpoint], theme, themeKey, transform);

    if (_style2 !== null) {
      var breakpointValue = minBreakpoint(breakpoints[breakpoint]);

      if (breakpointValue === null) {
        allStyle = merge(allStyle, _style2);
      } else {
        var _merge;

        allStyle = merge(allStyle, (_merge = {}, _merge[minWidth(breakpointValue)] = _style2, _merge));
      }
    }
  }

  return allStyle;
}

function getStyleFactory(prop, cssProperties, themeKey, transform) {
  return function getStyle(attrs, theme) {
    var value = attrs[prop];
    if (!is(value)) return null;
    cssProperties = cssProperties || [prop];
    var style = styleFromValue(cssProperties, value, theme, themeKey, transform);

    if (style !== null) {
      return style;
    }

    if (obj(value)) {
      return styleFromBreakPoint(cssProperties, value, theme, themeKey, transform);
    }

    return null;
  };
}

function style(_ref) {
  var prop = _ref.prop,
      cssProperties = _ref.cssProperties,
      _ref$themeKey = _ref.themeKey,
      themeKey = _ref$themeKey === void 0 ? null : _ref$themeKey,
      _ref$transform = _ref.transform,
      transform = _ref$transform === void 0 ? null : _ref$transform;
  var getStyle = getStyleFactory(prop, cssProperties, themeKey, transform);
  return createStyleGenerator(getStyle, [prop]);
}

function indexGeneratorsByProp(styles) {
  var index = {};

  for (var i = 0; i < styles.length; i++) {
    var _style3 = styles[i];

    if (_style3 && _style3.meta) {
      var propsKeys = Object.keys(_style3.meta.props);

      for (var j = 0; j < propsKeys.length; j++) {
        var prop = _style3.meta.props[propsKeys[j]];
        index[prop] = _style3;
      }
    }
  }

  return index;
}

function compose() {
  var flatGenerators = [];

  for (var _len = arguments.length, generators = new Array(_len), _key = 0; _key < _len; _key++) {
    generators[_key] = arguments[_key];
  }

  generators.forEach(function (gen) {
    if (gen.meta.generators) {
      flatGenerators = [].concat(flatGenerators, gen.meta.generators);
    } else {
      flatGenerators.push(gen);
    }
  });
  var generatorsByProp = indexGeneratorsByProp(flatGenerators);

  function getStyle(attrs, theme) {
    var propKeys = Object.keys(attrs);
    var propCount = propKeys.length;
    var allStyle = {};

    for (var i = 0; i < propCount; i++) {
      var propKey = propKeys[i];
      var generator = generatorsByProp[propKey];

      if (generator) {
        allStyle = merge(allStyle, generator.meta.getStyle(attrs, theme));
      }
    }

    return allStyle;
  }

  var props = flatGenerators.reduce(function (keys, generator) {
    return [].concat(keys, generator.meta.props);
  }, []);
  return createStyleGenerator(getStyle, props, generators);
}
function universal(generator) {
  var generatorsByProp = indexGeneratorsByProp([generator]);

  function genericGetStyle(opened) {
    return function getStyle(attrs, theme) {
      var propKeys = Object.keys(attrs);
      var propCount = propKeys.length;
      var allStyle = {};

      for (var i = 0; i < propCount; i++) {
        var propKey = propKeys[i];
        var _generator = generatorsByProp[propKey];
        allStyle = merge(allStyle, _generator ? _generator.meta.getStyle(attrs, theme) : opened ? getStyleFactory(propKey)(attrs, theme) : null);
      }

      return allStyle;
    };
  }

  var openedGetStyle = genericGetStyle(true);
  var restrictedGetStyle = genericGetStyle(false);
  var getStyles = createStyleGenerator(openedGetStyle, generator.meta.props, [generator]);

  getStyles.props = function (props) {
    var theme = props.theme || null;
    return restrictedGetStyle(props, theme, props);
  };

  return getStyles;
}

var background =
/*#__PURE__*/
style({
  prop: 'background'
});
var backgroundColor =
/*#__PURE__*/
style({
  prop: 'backgroundColor',
  themeKey: 'colors'
});
var backgroundImage =
/*#__PURE__*/
style({
  prop: 'backgroundImage'
});
var backgroundSize =
/*#__PURE__*/
style({
  prop: 'backgroundSize'
});
var backgroundPosition =
/*#__PURE__*/
style({
  prop: 'backgroundPosition'
});
var backgroundRepeat =
/*#__PURE__*/
style({
  prop: 'backgroundRepeat'
});
var backgrounds =
/*#__PURE__*/
compose(background, backgroundColor, backgroundImage, backgroundSize, backgroundPosition, backgroundRepeat);

var opacity =
/*#__PURE__*/
style({
  prop: 'opacity'
});
var overflow =
/*#__PURE__*/
style({
  prop: 'overflow'
});
var basics =
/*#__PURE__*/
compose(opacity, overflow);

var unit = function unit(_unit) {
  return function (value) {
    return num(value) ? "" + value + _unit : value;
  };
};
var px =
/*#__PURE__*/
unit('px');
var percent = function percent(n) {
  return !num(n) || n > 1 ? px(n) : n * 100 + "%";
};

var getBorder = function getBorder(n) {
  return num(n) && n > 0 ? n + "px solid" : n;
};

var border =
/*#__PURE__*/
style({
  prop: 'border',
  themeKey: 'borders',
  transform: getBorder
});
var borderTop =
/*#__PURE__*/
style({
  prop: 'borderTop',
  themeKey: 'borders',
  transform: getBorder
});
var borderTopColor =
/*#__PURE__*/
style({
  prop: 'borderTopColor',
  themeKey: 'colors'
});
var borderRight =
/*#__PURE__*/
style({
  prop: 'borderRight',
  themeKey: 'borders',
  transform: getBorder
});
var borderRightColor =
/*#__PURE__*/
style({
  prop: 'borderRightColor',
  themeKey: 'colors'
});
var borderBottom =
/*#__PURE__*/
style({
  prop: 'borderBottom',
  themeKey: 'borders',
  transform: getBorder
});
var borderBottomColor =
/*#__PURE__*/
style({
  prop: 'borderBottomColor',
  themeKey: 'colors'
});
var borderLeft =
/*#__PURE__*/
style({
  prop: 'borderLeft',
  themeKey: 'borders',
  transform: getBorder
});
var borderLeftColor =
/*#__PURE__*/
style({
  prop: 'borderLeftColor',
  themeKey: 'colors'
});
var borderColor =
/*#__PURE__*/
style({
  prop: 'borderColor',
  themeKey: 'colors'
});
var borderRadius =
/*#__PURE__*/
style({
  prop: 'borderRadius',
  themeKey: 'radii',
  transform: px
});
var boxShadow =
/*#__PURE__*/
style({
  prop: 'boxShadow',
  themeKey: 'shadows'
});
var borders =
/*#__PURE__*/
compose(border, borderTop, borderRight, borderBottom, borderLeft, borderColor, borderRadius, boxShadow);

var width =
/*#__PURE__*/
style({
  prop: 'width',
  transform: percent,
  themeKey: 'widths'
});
var height =
/*#__PURE__*/
style({
  prop: 'height',
  transform: percent,
  themeKey: 'heights'
});
var maxWidth =
/*#__PURE__*/
style({
  prop: 'maxWidth',
  transform: percent,
  themeKey: 'widths'
});
var maxHeight =
/*#__PURE__*/
style({
  prop: 'maxHeight',
  transform: percent,
  themeKey: 'heights'
});
var minWidth$1 =
/*#__PURE__*/
style({
  prop: 'minWidth',
  transform: percent,
  themeKey: 'widths'
});
var minHeight =
/*#__PURE__*/
style({
  prop: 'minHeight',
  transform: percent,
  themeKey: 'heights'
});
var dimensions =
/*#__PURE__*/
compose(width, height, maxWidth, maxHeight, minWidth$1, minHeight);

var display =
/*#__PURE__*/
style({
  prop: 'display'
});
var alignItems =
/*#__PURE__*/
style({
  prop: 'alignItems'
});
var alignContent =
/*#__PURE__*/
style({
  prop: 'alignContent'
});
var justifyContent =
/*#__PURE__*/
style({
  prop: 'justifyContent'
});
var flexWrap =
/*#__PURE__*/
style({
  prop: 'flexWrap'
});
var flexBasis =
/*#__PURE__*/
style({
  prop: 'flexBasis',
  transform: percent
});
var flexDirection =
/*#__PURE__*/
style({
  prop: 'flexDirection'
});
var flex =
/*#__PURE__*/
style({
  prop: 'flex'
});
var justifySelf =
/*#__PURE__*/
style({
  prop: 'justifySelf'
});
var alignSelf =
/*#__PURE__*/
style({
  prop: 'alignSelf'
});
var order =
/*#__PURE__*/
style({
  prop: 'order'
});
var flexboxes =
/*#__PURE__*/
compose(display, alignItems, alignContent, justifyContent, flexWrap, flexBasis, flexDirection, flex, justifySelf, alignSelf, order);

var position =
/*#__PURE__*/
style({
  prop: 'position'
});
var zIndex =
/*#__PURE__*/
style({
  prop: 'zIndex',
  themeKey: 'zIndexes'
});
var top =
/*#__PURE__*/
style({
  prop: 'top',
  transform: px
});
var right =
/*#__PURE__*/
style({
  prop: 'right',
  transform: px
});
var bottom =
/*#__PURE__*/
style({
  prop: 'bottom',
  transform: px
});
var left =
/*#__PURE__*/
style({
  prop: 'left',
  transform: px
});
var positions =
/*#__PURE__*/
compose(position, zIndex, top, right, bottom, left);

var DEFAULT_SPACING = [0, 8, 16, 24, 32, 40, 48, 56, 64, 72];

function transform(transformedValue, _ref) {
  var rawValue = _ref.rawValue,
      _ref$variants = _ref.variants,
      variants = _ref$variants === void 0 ? DEFAULT_SPACING : _ref$variants;

  if (!num(rawValue)) {
    return variants[rawValue] || rawValue;
  }

  var abs = Math.abs(rawValue);
  var neg = negative(rawValue);
  var value = variants[abs] || abs;

  if (!num(value)) {
    return neg ? "-" + value : value;
  }

  return value * (neg ? -1 : 1);
}

var themeKey = 'spaces';
var m =
/*#__PURE__*/
style({
  prop: 'm',
  cssProperties: ['margin'],
  themeKey: themeKey,
  transform: transform
});
var mt =
/*#__PURE__*/
style({
  prop: 'mt',
  cssProperties: ['marginTop'],
  themeKey: themeKey,
  transform: transform
});
var mr =
/*#__PURE__*/
style({
  prop: 'mr',
  cssProperties: ['marginRight'],
  themeKey: themeKey,
  transform: transform
});
var mb =
/*#__PURE__*/
style({
  prop: 'mb',
  cssProperties: ['marginBottom'],
  themeKey: themeKey,
  transform: transform
});
var ml =
/*#__PURE__*/
style({
  prop: 'ml',
  cssProperties: ['marginLeft'],
  themeKey: themeKey,
  transform: transform
});
var mx =
/*#__PURE__*/
style({
  prop: 'mx',
  cssProperties: ['marginRight', 'marginLeft'],
  themeKey: themeKey,
  transform: transform
});
var my =
/*#__PURE__*/
style({
  prop: 'my',
  cssProperties: ['marginTop', 'marginBottom'],
  themeKey: themeKey,
  transform: transform
});
var p =
/*#__PURE__*/
style({
  prop: 'p',
  cssProperties: ['padding'],
  themeKey: themeKey,
  transform: transform
});
var pt =
/*#__PURE__*/
style({
  prop: 'pt',
  cssProperties: ['paddingTop'],
  themeKey: themeKey,
  transform: transform
});
var pr =
/*#__PURE__*/
style({
  prop: 'pr',
  cssProperties: ['paddingRight'],
  themeKey: themeKey,
  transform: transform
});
var pb =
/*#__PURE__*/
style({
  prop: 'pb',
  cssProperties: ['paddingBottom'],
  themeKey: themeKey,
  transform: transform
});
var pl =
/*#__PURE__*/
style({
  prop: 'pl',
  cssProperties: ['paddingLeft'],
  themeKey: themeKey,
  transform: transform
});
var px$1 =
/*#__PURE__*/
style({
  prop: 'px',
  cssProperties: ['paddingRight', 'paddingLeft'],
  themeKey: themeKey,
  transform: transform
});
var py =
/*#__PURE__*/
style({
  prop: 'py',
  cssProperties: ['paddingTop', 'paddingBottom'],
  themeKey: themeKey,
  transform: transform
});
var space =
/*#__PURE__*/
compose(m, mt, mr, mb, ml, mx, my, p, pt, pr, pb, pl, px$1, py);

var fontFamily =
/*#__PURE__*/
style({
  prop: 'fontFamily',
  themeKey: 'fonts'
});
var fontSize =
/*#__PURE__*/
style({
  prop: 'fontSize',
  themeKey: 'fontSizes'
});
var lineHeight =
/*#__PURE__*/
style({
  prop: 'lineHeight',
  themeKey: 'lineHeights'
});
var fontWeight =
/*#__PURE__*/
style({
  prop: 'fontWeight',
  themeKey: 'fontWeights'
});
var textAlign =
/*#__PURE__*/
style({
  prop: 'textAlign'
});
var letterSpacing =
/*#__PURE__*/
style({
  prop: 'letterSpacing',
  themeKey: 'letterSpacings',
  transform: px
});
var color =
/*#__PURE__*/
style({
  prop: 'color',
  themeKey: 'colors'
});
var textTransform =
/*#__PURE__*/
style({
  prop: 'textTransform'
});
var typography =
/*#__PURE__*/
compose(fontFamily, fontSize, lineHeight, fontWeight, textAlign, letterSpacing, color, textTransform);

var system =
/*#__PURE__*/
compose(backgrounds, basics, borders, dimensions, flexboxes, positions, space, typography);

var cx = function cx(styles) {
  return function (propsOrTheme) {
    return styles.map(function (style) {
      return typeof style === 'function' ? style(propsOrTheme) : style;
    });
  };
};

var universalSystem =
/*#__PURE__*/
universal(system);

export default universalSystem;
export { alignContent, alignItems, alignSelf, background, backgroundColor, backgroundImage, backgroundPosition, backgroundRepeat, backgroundSize, backgrounds, basics, border, borderBottom, borderBottomColor, borderColor, borderLeft, borderLeftColor, borderRadius, borderRight, borderRightColor, borderTop, borderTopColor, borders, bottom, boxShadow, color, compose, cx, dimensions, display, flex, flexBasis, flexDirection, flexWrap, flexboxes, fontFamily, fontSize, fontWeight, height, justifyContent, justifySelf, left, letterSpacing, lineHeight, m, maxHeight, maxWidth, mb, merge, minHeight, minWidth$1 as minWidth, ml, mr, mt, mx, my, opacity, order, overflow, p, pb, pl, position, positions, pr, pt, px$1 as px, py, right, space, style, system, textAlign, textTransform, top, typography, universal, width, zIndex };
